import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useFriends, Friendship, FriendChallenge } from "@/hooks/useFriends";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Swords, Trophy, Zap, BookOpen, Lightbulb, Flame, Target,
  Clock, Check, X, RotateCcw, Users, ChevronRight, Loader2, Hourglass, Eye,
  Percent, Award,
} from "lucide-react";
import { NewChallengeModal } from "@/components/social/NewChallengeModal";
import { WeeklyRecapCard } from "@/components/social/WeeklyRecapCard";
import { FriendLeaderboard } from "@/components/social/FriendLeaderboard";
import { LeaguePanel } from "@/components/social/LeaguePanel";
import { SeasonPassPanel } from "@/components/social/SeasonPassPanel";
import { ChallengeTheGuides } from "@/components/social/InstructorOpponents";
import { Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PageShell from "@/components/premium/PageShell";
import PageHeader from "@/components/premium/PageHeader";
import type { AnyValue } from "@/lib/rowTypes";

type Tab = "active" | "pending" | "completed" | "rivalries" | "league" | "season";

const TYPE_META: Record<string, { icon: AnyValue; label: string; tone: string }> = {
  topic_battle: { icon: BookOpen, label: "Topic Battle", tone: "text-primary" },
  scenario_showdown: { icon: Lightbulb, label: "Scenario Showdown", tone: "text-accent" },
  daily_duel: { icon: Zap, label: "Daily Duel", tone: "text-secondary" },
  streak_war: { icon: Flame, label: "Streak War", tone: "text-orange-400" },
  milestone: { icon: Target, label: "Milestone", tone: "text-emerald-400" },
  quiz_duel: { icon: Swords, label: "Quiz Duel", tone: "text-primary" },
  xp_race: { icon: Zap, label: "XP Race", tone: "text-secondary" },
  speed_round: { icon: Zap, label: "Speed Round", tone: "text-secondary" },
  accuracy: { icon: Target, label: "Accuracy", tone: "text-primary" },
};

/**
 * Derives the true state of a challenge from the user's perspective.
 * Backend status alone is not enough — we also look at per-side completion
 * timestamps to know whether the user can act NOW or is waiting.
 */
type DerivedState =
  | "awaiting_acceptance_outgoing"   // I sent it, opponent hasn't accepted
  | "awaiting_acceptance_incoming"   // They sent it, I need to accept
  | "my_turn_ready"                  // Accepted, I can play now
  | "waiting_on_opponent"            // I finished my turn, opponent hasn't
  | "metric_in_progress"             // Time/metric race — nothing to "play"
  | "completed"                      // Resolved
  | "expired_or_canceled";

const METRIC_TYPES = new Set(["xp_race", "streak_war", "milestone"]);

function deriveChallengeState(c: FriendChallenge, userId: string | undefined): DerivedState {
  if (!userId) return "expired_or_canceled";
  const isChallenger = c.challenger_id === userId;
  const isFinal = c.status === "completed" || c.status === "expired";
  if (c.status === "expired") return "expired_or_canceled";
  if (isFinal) return "completed";
  if (c.status === "declined" || c.status === "canceled") return "expired_or_canceled";

  if (c.status === "pending") {
    return isChallenger ? "awaiting_acceptance_outgoing" : "awaiting_acceptance_incoming";
  }

  // status === "active"
  if (METRIC_TYPES.has(c.challenge_type)) return "metric_in_progress";

  const myCompletedAt = isChallenger ? c.challenger_completed_at : c.challenged_completed_at;
  if (myCompletedAt) return "waiting_on_opponent";
  return "my_turn_ready";
}

const STATE_COPY: Record<DerivedState, { badge: string; status: string; tone: string }> = {
  awaiting_acceptance_outgoing: {
    badge: "Awaiting Opponent",
    status: "Challenge sent. Waiting for opponent to accept.",
    tone: "bg-muted/50 text-muted-foreground border-border",
  },
  awaiting_acceptance_incoming: {
    badge: "Your Move",
    status: "An opponent challenged you. Accept to begin.",
    tone: "bg-accent/10 text-accent border-accent/30",
  },
  my_turn_ready: {
    badge: "Your Move",
    status: "Your turn is ready.",
    tone: "bg-accent/10 text-accent border-accent/30",
  },
  waiting_on_opponent: {
    badge: "Awaiting Opponent",
    status: "Your turn is complete. Waiting for opponent.",
    tone: "bg-muted/50 text-muted-foreground border-border",
  },
  metric_in_progress: {
    badge: "In Progress",
    status: "Race in progress. Resolves when the timer ends or the goal is hit.",
    tone: "bg-secondary/10 text-secondary-foreground border-border",
  },
  completed: {
    badge: "Complete",
    status: "Challenge complete. View results.",
    tone: "bg-primary/10 text-primary border-primary/30",
  },
  expired_or_canceled: {
    badge: "Closed",
    status: "This challenge is no longer active.",
    tone: "bg-muted/40 text-muted-foreground border-border",
  },
};

const ChallengeCenter = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { friends, challenges, loading, acceptChallenge, declineChallenge, rematchChallenge, getRivalries } = useFriends();

  const initialTab = (searchParams.get("tab") as Tab) || "active";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState<Friendship | null>(null);
  const [rivalries, setRivalries] = useState<Record<string, AnyValue>[]>([]);
  const [loadingRivals, setLoadingRivals] = useState(false);

  useEffect(() => {
    if (tab !== "rivalries") return;
    setLoadingRivals(true);
    getRivalries().then(r => { setRivalries(r); setLoadingRivals(false); });
  }, [tab]); // eslint-disable-line

  // Re-bucket by what the user can actually do RIGHT NOW, not raw backend status.
  // Active = the user can act this second.
  // Pending = waiting on someone (acceptance OR opponent's turn OR a timed race).
  // History = resolved/expired/canceled.
  const enriched = challenges.map(c => ({ c, derived: deriveChallengeState(c, user?.id) }));
  const active = enriched.filter(e => e.derived === "my_turn_ready" || e.derived === "awaiting_acceptance_incoming").map(e => e.c);
  const pending = enriched.filter(e =>
    e.derived === "awaiting_acceptance_outgoing" ||
    e.derived === "waiting_on_opponent" ||
    e.derived === "metric_in_progress"
  ).map(e => e.c);
  const completed = enriched.filter(e => e.derived === "completed" || e.derived === "expired_or_canceled").map(e => e.c);
  const incomingPending = enriched.filter(e => e.derived === "awaiting_acceptance_incoming").map(e => e.c);

  // Career challenge totals
  const finalized = challenges.filter(c => c.status === "completed" || c.status === "expired");
  const wins = finalized.filter(c => c.winner_id === user?.id).length;
  const losses = finalized.filter(c => c.winner_id && c.winner_id !== user?.id).length;
  const decided = wins + losses;
  const winRate = decided > 0 ? Math.round((wins / decided) * 100) : null;

  const launchPicker = () => {
    if (friends.length === 0) {
      toast.error("Add a friend first to send a challenge");
      navigate("/friends");
      return;
    }
    setPicking(true);
  };

  const handleEnter = (c: FriendChallenge) => {
    const derived = deriveChallengeState(c, user?.id);
    if (derived === "metric_in_progress") {
      toast.info("This is a metric-based race. Check back when the timer ends or both players hit the goal.");
      return;
    }
    if (derived === "completed" || derived === "expired_or_canceled") {
      navigate(`/challenge/${c.id}`);
      return;
    }
    if (derived === "awaiting_acceptance_outgoing") {
      toast.info("Waiting for your opponent to accept this challenge.");
      return;
    }
    if (derived === "waiting_on_opponent") {
      toast.info("You've finished your turn. Waiting on your opponent.");
      return;
    }
    navigate(`/challenge/${c.id}`);
  };

  const renderChallengeCard = (c: FriendChallenge) => {
    const meta = TYPE_META[c.challenge_type] || TYPE_META.quiz_duel;
    const Icon = meta.icon;
    const isChallenger = c.challenger_id === user?.id;
    const opponentName = isChallenger ? c.challenged_name : c.challenger_name;
    const myScore = isChallenger ? c.challenger_score : c.challenged_score;
    const theirScore = isChallenger ? c.challenged_score : c.challenger_score;
    const isFinal = c.status === "completed" || c.status === "expired";
    const isTie = isFinal && (c.result_state === "tie" || (!c.winner_id && c.result_state !== "expired"));
    const isExpired = c.result_state === "expired" || (c.status === "expired" && !c.winner_id);
    const iWon = !!c.winner_id && c.winner_id === user?.id;
    const iLost = !!c.winner_id && c.winner_id !== user?.id;
    const finalizedDate = c.finalized_at ? new Date(c.finalized_at).toLocaleDateString() : null;
    const derived = deriveChallengeState(c, user?.id);
    const copy = STATE_COPY[derived];
    const isIncoming = derived === "awaiting_acceptance_incoming";
    const canPlayNow = derived === "my_turn_ready";

    const resultLabel = iWon ? "You Won" : iLost ? "You Lost" : isExpired ? "Expired" : isTie ? "Tie" : null;
    const resultClass =
      iWon ? "bg-primary/10 text-primary border-primary/30"
      : iLost ? "bg-destructive/10 text-destructive border-destructive/30"
      : "bg-muted/40 text-muted-foreground border-border";

    return (
      <div key={c.id} className={cn(
        "glass-card p-4 border",
        isFinal && iWon && "border-primary/40",
        isFinal && iLost && "border-destructive/30",
        isFinal && (isTie || isExpired) && "border-border",
        canPlayNow && "border-accent/40",
        derived === "awaiting_acceptance_incoming" && "border-accent/30",
      )}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn("w-4 h-4 shrink-0", meta.tone)} />
            <div className="min-w-0">
              <p className="text-sm font-heading font-semibold text-foreground truncate">{meta.label}</p>
              <p className="text-xs text-muted-foreground truncate">vs {opponentName}</p>
            </div>
          </div>
          <span className={cn(
            "text-[10px] px-2 py-0.5 rounded-md font-semibold uppercase tracking-wider shrink-0 border",
            copy.tone,
          )}>{copy.badge}</span>
        </div>

        {(c.status === "active" || isFinal) && (
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-baseline gap-2 text-sm tabular-nums">
              <span className={cn("font-bold text-base", iWon ? "text-primary" : "text-foreground")}>{myScore}</span>
              <span className="text-muted-foreground">–</span>
              <span className={cn("font-bold text-base", iLost ? "text-destructive" : "text-foreground")}>{theirScore}</span>
              <span className="text-[11px] text-muted-foreground ml-1">You vs {opponentName}</span>
            </div>
            {isFinal && resultLabel && (
              <span className={cn(
                "text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md border",
                resultClass,
              )}>
                {resultLabel}
              </span>
            )}
          </div>
        )}

        {isFinal && isTie && (
          <p className="text-[11px] text-muted-foreground mb-2">Tied score — no automatic tiebreaker applied.</p>
        )}
        {isFinal && finalizedDate && (
          <p className="text-[11px] text-muted-foreground/70 mb-2">{finalizedDate}</p>
        )}

        {/* Plain-language status line — always tells the truth about what's happening. */}
        {!isFinal && (
          <p className="text-[11px] text-muted-foreground mb-3 leading-snug">{copy.status}</p>
        )}

        <div className="flex flex-wrap gap-2">
          {isIncoming && (
            <>
              <Button size="sm" onClick={() => acceptChallenge(c.id)}>
                <Check className="w-3 h-3 mr-1" /> Accept
              </Button>
              <Button size="sm" variant="outline" onClick={() => declineChallenge(c.id)}>
                <X className="w-3 h-3" />
              </Button>
            </>
          )}
          {canPlayNow && (
            <Button size="sm" onClick={() => handleEnter(c)}>
              <ChevronRight className="w-3 h-3 mr-1" /> Resume Challenge
            </Button>
          )}
          {derived === "waiting_on_opponent" && (
            <Button size="sm" variant="outline" disabled className="opacity-70">
              <Hourglass className="w-3 h-3 mr-1" /> Waiting for Opponent
            </Button>
          )}
          {derived === "awaiting_acceptance_outgoing" && (
            <Button size="sm" variant="outline" disabled className="opacity-70">
              <Hourglass className="w-3 h-3 mr-1" /> Awaiting Opponent
            </Button>
          )}
          {derived === "metric_in_progress" && (
            <Button size="sm" variant="outline" disabled className="opacity-70">
              <Clock className="w-3 h-3 mr-1" /> In Progress
            </Button>
          )}
          {isFinal && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/challenge/${c.id}`)}>
              <Eye className="w-3 h-3 mr-1" /> View Results
            </Button>
          )}
          {isFinal && (
            <Button size="sm" variant="outline" onClick={async () => {
              const id = await rematchChallenge(c.id);
              if (id) navigate(`/challenge/${id}`);
            }}>
              <RotateCcw className="w-3 h-3 mr-1" /> Rematch
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Challenge Center"
        subtitle="Test your knowledge, challenge your friends, and build mastery."
        icon={Swords}
        actions={
          <Button size="sm" onClick={launchPicker}>
            <Swords className="w-4 h-4 mr-1" /> New Challenge
          </Button>
        }
      />
        {/* Premium summary strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <CCSnap icon={Zap} label="Active" value={active.length} sub={incomingPending.length > 0 ? `${incomingPending.length} need your move` : "ready to play"} tone="accent" />
          <CCSnap icon={Hourglass} label="Pending" value={pending.length} sub={pending.length === 0 ? "no waiting matches" : "in flight"} tone="muted" />
          <CCSnap icon={Trophy} label="Wins" value={wins} sub={losses > 0 ? `${losses} losses` : "career wins"} tone="primary" />
          <CCSnap icon={Percent} label="Win Rate" value={winRate !== null ? `${winRate}%` : "—"} sub={decided === 0 ? "play to see stats" : `${decided} decided`} tone="secondary" />
        </div>

        {/* Retention surfaces */}
        <div className="grid lg:grid-cols-2 gap-4">
          <WeeklyRecapCard />
          <FriendLeaderboard windowDays={7} />
        </div>

        {/* Computer opponents — always-available practice */}
        <ChallengeTheGuides />

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {([
            { key: "active" as Tab, label: "Active", count: active.length, icon: Zap },
            { key: "pending" as Tab, label: "Pending", count: pending.length, icon: Clock },
            { key: "completed" as Tab, label: "History", count: 0, icon: Trophy },
            { key: "rivalries" as Tab, label: "Rivalries", count: 0, icon: Users },
            { key: "league" as Tab, label: "League", count: 0, icon: Crown },
            { key: "season" as Tab, label: "Season", count: 0, icon: Sparkles },
          ]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap",
                tab === t.key ? "bg-primary/10 text-primary border border-primary/30" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.count > 0 && (
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Friend picker (initial state from launchPicker) */}
        {picking && (
          <div className="glass-card p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-heading text-sm font-semibold text-foreground">Choose a friend</p>
              <Button variant="ghost" size="sm" onClick={() => setPicking(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {friends.map(f => (
                <button key={f.id} onClick={() => { setTarget(f); setPicking(false); }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/30 transition text-left">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                    {(f.friend_profile?.display_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{f.friend_profile?.display_name || "Anonymous"}</p>
                    {f.is_household && <p className="text-[10px] text-accent uppercase tracking-wide">Family</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "active" && (
          active.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Swords className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground mb-4">No active challenges. Send one to a friend.</p>
              <Button onClick={launchPicker}><Swords className="w-4 h-4 mr-1" /> Start Challenge</Button>
            </div>
          ) : <div className="grid sm:grid-cols-2 gap-3">{active.map(renderChallengeCard)}</div>
        )}

        {tab === "pending" && (
          pending.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No pending challenges.</p>
            </div>
          ) : <div className="grid sm:grid-cols-2 gap-3">{pending.map(renderChallengeCard)}</div>
        )}

        {tab === "completed" && (
          completed.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Trophy className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No completed challenges yet.</p>
            </div>
          ) : <div className="grid sm:grid-cols-2 gap-3">{completed.map(renderChallengeCard)}</div>
        )}

        {tab === "rivalries" && (
          loadingRivals ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : rivalries.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No rivalries yet. Complete challenges to build your record.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rivalries.map(r => {
                const winRate = r.total > 0 ? Math.round((r.my_wins / r.total) * 100) : 0;
                const lastResult = r.last_result as string | null;
                const lastIWon = r.last_winner_id && r.last_winner_id === user?.id;
                const lastILost = r.last_winner_id && r.last_winner_id !== user?.id;
                const lastTie = lastResult === "tie";
                const lastExpired = lastResult === "expired";
                const lastLabel = lastIWon ? "You Won" : lastILost ? "You Lost" : lastExpired ? "Expired" : lastTie ? "Tie" : null;
                const lastLabelClass =
                  lastIWon ? "bg-primary/10 text-primary border-primary/30"
                  : lastILost ? "bg-destructive/10 text-destructive border-destructive/30"
                  : "bg-muted/40 text-muted-foreground border-border";
                const typeMeta = r.last_type ? TYPE_META[r.last_type as string] : null;
                return (
                  <div key={r.opponent_id} className="glass-card p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        {(r.opponent_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold text-foreground truncate">{r.opponent_name || "Anonymous"}</p>
                        <p className="text-[11px] text-muted-foreground">{r.total} duels · {winRate}% win rate</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="rounded-lg border border-primary/20 bg-primary/5 p-2 text-center">
                        <p className="text-lg font-bold text-primary tabular-nums">{r.my_wins}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Wins</p>
                      </div>
                      <div className="rounded-lg border border-border bg-muted/20 p-2 text-center">
                        <p className="text-lg font-bold text-foreground tabular-nums">{r.ties}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Ties</p>
                      </div>
                      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-2 text-center">
                        <p className="text-lg font-bold text-destructive tabular-nums">{r.their_wins}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Losses</p>
                      </div>
                    </div>

                    {lastLabel && (
                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Last:</span>
                          {typeMeta && <typeMeta.icon className={cn("w-3 h-3 shrink-0", typeMeta.tone)} />}
                          <span className="text-xs text-foreground truncate">{typeMeta?.label || "Match"}</span>
                          {(r.last_my_score !== null && r.last_my_score !== undefined && r.last_their_score !== null && r.last_their_score !== undefined) && (
                            <span className="text-xs font-bold text-foreground tabular-nums shrink-0">
                              {r.last_my_score}–{r.last_their_score}
                            </span>
                          )}
                        </div>
                        <span className={cn(
                          "text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md border shrink-0",
                          lastLabelClass,
                        )}>{lastLabel}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === "league" && <LeaguePanel />}
        {tab === "season" && <SeasonPassPanel />}

      <NewChallengeModal open={!!target} onOpenChange={(v) => !v && setTarget(null)} friend={target} />
    </PageShell>
  );
};

export default ChallengeCenter;

type CCTone = "primary" | "secondary" | "accent" | "muted";
const CC_TONES: Record<CCTone, { icon: string; ring: string; glow: string }> = {
  primary:   { icon: "text-primary",   ring: "border-primary/25",   glow: "bg-primary/10" },
  secondary: { icon: "text-secondary", ring: "border-secondary/25", glow: "bg-secondary/10" },
  accent:    { icon: "text-accent",    ring: "border-accent/25",    glow: "bg-accent/10" },
  muted:     { icon: "text-muted-foreground", ring: "border-border", glow: "bg-muted/20" },
};

function CCSnap({
  icon: Icon, label, value, sub, tone = "primary",
}: { icon: AnyValue; label: string; value: number | string; sub?: string; tone?: CCTone }) {
  const s = CC_TONES[tone];
  return (
    <div className={cn("glass-card relative overflow-hidden p-3 sm:p-4 border", s.ring)}>
      <div className={cn("absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl pointer-events-none", s.glow)} />
      <div className="relative flex items-start gap-2.5">
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center border", s.ring, s.glow)}>
          <Icon className={cn("w-4 h-4", s.icon)} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
          <p className="text-xl font-heading font-bold text-foreground tabular-nums leading-tight">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}
